import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { de } from "date-fns/locale";
import { getTripPhase } from "../utils/getTripPhase";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../lib/supabaseClient";
import { useUserProfile } from "../contexts/UserProfileContext";
import BlogPosts from "../components/BlogPosts";
import DevDashboard from "../components/dev/DevDashboard";
import FloatingActionButton from "../components/FloatingActionButton";
import CreateTripModal from "../components/CreateTripModal";
import { usePushNotifications } from "../lib/usePushNotifications";
import { sendPushNotification } from "../lib/sendPushNotification";
import PushPermissionModal from "../components/PushPermissionModal";
import CurrentQuiz from "../components/CurrentQuiz";
import NotificationCard from "../components/NotificationCard";
import { useConfirmDialog } from "../contexts/ConfirmDialogContext";
import { Trip, TripWithRole, TripJoinEntry, TripRequest } from "../types";
import TripSelectModal from "../components/TripSelectModal";
import PollUserView from "../components/PollUserView";
import { useFeatureAccess } from "../hooks/useFeatureAccess";
import AiTripPlanningModal from "../components/AiTripPlanningModal";


// Kleiner Inline-Hook: liest feature_flags.status === 'enabled'
  function useGlobalFlagInline(featureKey: string): boolean | null {
    const [enabled, setEnabled] = useState<boolean | null>(null);

    useEffect(() => {
      let alive = true;
      (async () => {
        const { data, error } = await supabase
          .from("feature_flags")
          .select("status")
          .eq("feature_key", featureKey)
          .maybeSingle();
        if (!alive) return;
        if (error) {
          console.warn("useGlobalFlagInline failed", featureKey, error);
          setEnabled(false);
          return;
        }
        setEnabled((data?.status ?? "").toLowerCase() === "enabled");
      })();
      return () => { alive = false; };
    }, [featureKey]);

    return enabled; // true | false | null (loading)
  }

  

async function getOrganizableTrips(userId: string): Promise<TripWithRole[]> {
  // 1. Eigene Reisen
  const { data: createdTrips, error: err1 } = await supabase
    .from("trips")
    .select("*")
    .eq("creator_id", userId);

  // 2. Co-Orga-Reisen
  const { data: orgaTrips, error: err2 } = await supabase
    .from("trip_participants")
    .select("trips(*), is_co_organizer")
    .eq("user_id", userId)
    .eq("is_co_organizer", true);

  if (err1 || err2) {
    console.error("Fehler beim Laden der organisierbaren Trips", err1 || err2);
    return [];
  }

  const tripsAsCreator: TripWithRole[] = (createdTrips || []).map((trip) => ({
    ...trip,
    role: "creator",
  }));

  const tripsAsOrga = (orgaTrips || [])
    .map((entry): TripWithRole | undefined => {
      const trip = Array.isArray(entry.trips) ? entry.trips[0] : entry.trips;
      if (trip && trip.id && trip.name) {
        return {
          ...(trip as Trip),
          role: "co_organizer",
        };
      }
      return undefined;
    })
    .filter((t): t is TripWithRole => Boolean(t));

  return [...tripsAsCreator, ...tripsAsOrga];
}

async function getAllJoinedTrips(userId: string): Promise<TripWithRole[]> {
  const { data, error } = await supabase
    .from("trip_participants")
    .select("trips(*), is_co_organizer")
    .eq("user_id", userId)
    .or("is_blocked.is.false,is_blocked.is.null");

  if (error) {
    console.error("❌ Fehler beim Laden der Teilnehmer-Trips", error.message);
    return [];
  }

  return (data ?? [])
    .map((entry): TripWithRole | undefined => {
      const trip = Array.isArray(entry.trips) ? entry.trips[0] : entry.trips;
      if (!trip || !trip.id) return undefined;

      return {
        ...(trip as Trip),
        role: entry.is_co_organizer ? "co_organizer" : "participant",
      };
    })
    .filter((t): t is TripWithRole => !!t);
}

export async function sendTestNotification(userId: string) {
  const { data, error } = await supabase
    .from("user_push_tokens")
    .select("token")
    .eq("user_id", userId)
    .order("updated_at", { ascending: false }) // Falls mehrere Geräte
    .limit(1)
    .single();

  if (error || !data?.token) {
    console.error("❌ Kein Token gefunden:", error?.message);
    alert("❌ Kein Push-Token gefunden");
    return;
  }

  try {
    await sendPushNotification(
      data.token,
      "🎉 Testnachricht",
      "Hallo! Wenn du das siehst, funktionieren Push-Benachrichtigungen. 💬"
    );
    alert("✅ Push gesendet!");
  } catch (err) {
    console.error("❌ Fehler beim Senden:", err);
    alert("❌ Fehler beim Senden");
  }
}

export default function Dashboard() {
  const { user } = useAuth();
  // Direkt aufrufen – nur wenn user vorhanden
  const { showConfirm } = useConfirmDialog();

  usePushNotifications(user?.id ?? null);


  const [trips, setTrips] = useState<Trip[]>([]);
  const [loading, setLoading] = useState(true);
  const [firstName, setFirstName] = useState<string | null>(null);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { profile } = useUserProfile();
  const [showBlog, setShowBlog] = useState(false);
  const [coOrganizerMap, setCoOrganizerMap] = useState<Map<string, boolean>>(
    new Map()
  );
  const [showCreateModal, setShowCreateModal] = useState(false);

  // BottomTabBar + Button: ?create=true öffnet CreateTripModal
  useEffect(() => {
    if (searchParams.get("create") === "true") {
      setShowCreateModal(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  const [pendingTrips, setPendingTrips] = useState<Trip[]>([]);
  const [dashboardContext, setDashboardContext] = useState<string | null>(null);

  const [organizableTrips, setOrganizableTrips] = useState<TripWithRole[]>([]);
  const [allTrips, setAllTrips] = useState<TripWithRole[]>([]); // ⬅️ nach oben ziehen!
  const [showTripSelectModal, setShowTripSelectModal] = useState(false);

  // Dashboard Extras: Offene Aktionen + Aktivitäts-Feed
  const [openTodos, setOpenTodos] = useState<{ id: string; title: string; trip_id: string; trip_name: string }[]>([]);
  const [myDebts, setMyDebts] = useState<{ trip_id: string; trip_name: string; total: number }[]>([]);
  const [crossFeed, setCrossFeed] = useState<{ id: string; icon: string; description: string; trip_name: string; created_at: string }[]>([]);

  // mindestens ein Trip mit Rolle ...
  const hasAnyTrip = allTrips.length > 0;
  const hasOrgaTrip = allTrips.some(
    (trip) => trip.role === "creator" || trip.role === "co_organizer"
  );

  // Wartelisten-Modal
  const [showWaitlistModal, setShowWaitlistModal] = useState(false);

  // Feature: darf User Trips erstellen?
  const canCreateTrips = useFeatureAccess("create_trips"); // true | false | null

  // Globales Flag: ist Wartelisten-Modus aktiv?
  const waitlistEnabled = useGlobalFlagInline("mvp_waitlist_enabled"); // true | false | null

  // KI Reiseplanung Feature-Flag
  const globalAiPlanFlag = useGlobalFlagInline("ai_trip_planning");
  const userAiPlanFlag = useFeatureAccess("ai_trip_planning");
  const showAiPlanning = (globalAiPlanFlag === true) || (userAiPlanFlag === true);
  const [showAiPlanningModal, setShowAiPlanningModal] = useState(false);

  // Invite-Ausnahme (kommt später via Context)
  const inviteActive = false;

  // Entscheidet, ob wir den "Neue Reise"-Flow blocken und stattdessen das Wartelisten-Modal zeigen
  const gateCreate = useMemo(() => {
    // Solange einer der Werte noch lädt (null), NICHT blocken
    if (canCreateTrips === null || waitlistEnabled === null) return false;
    // Eingeladene Gäste niemals blocken
    if (inviteActive) return false;
    // Gate aktiv, wenn Nutzer KEIN create_trips hat und Waitlist global aktiv ist
    return canCreateTrips === false && waitlistEnabled === true;
  }, [canCreateTrips, waitlistEnabled, inviteActive]);


  //Schreibt Kommentar in DB
    const handleWaitlistSubmit = async (msg: string) => {
    // User holen (nur zur Sicherheit)
    const { data: auth } = await supabase.auth.getUser();
    const user = auth?.user;
    if (!user) {
      // Optional: sanft zum Login schicken
      // navigate(`/?redirect=/dashboard`);
      alert("Bitte logge dich ein oder registriere dich zuerst.");
      return;
    }

    // Insert in die neue Tabelle (RLS erlaubt nur: user_id = auth.uid())
    const { error } = await supabase.from("waitlist_requests").insert({
      user_id: user.id,
      message: msg,
      // status: 'open' // (Default greift ohnehin)
    });

    if (error) {
      console.error("Waitlist-Submit failed:", error.message);
      // Optional: Toast / Banner
      // toast.error("Konnte deine Nachricht nicht speichern.");
    } else {
      // Optional: Toast / Banner
      // toast.success("Danke! Wir melden uns.");
    }

    setShowWaitlistModal(false);
  };



  useEffect(() => {
    if (!dashboardContext || !user?.id) return;

    if (dashboardContext === "add_suggestion") {
      // nur Teilnehmer-Rollen nötig
      getAllJoinedTrips(user.id).then(setOrganizableTrips);
    } else {
      // nur Orga-Rollen
      getOrganizableTrips(user.id).then(setOrganizableTrips);
    }
  }, [dashboardContext, user?.id]);

  const ARCHIVE_AFTER_DAYS = 14;
  const [showArchive, setShowArchive] = useState(false);
  const [importantNotifications, setImportantNotifications] = useState<
    {
      id: string;
      title: string;
      message: string;
      user_id: string | null;
      created_at: string | null;
      trip_id: string | null;
      trip: { name: string } | null;
      author: { first_name: string; last_name: string } | null;
    }[]
  >([]);

  



  const fetchTrips = async () => {
    if (!user) return;

    // 🔁 3.1 Offene Teilnahmeanfragen
    const { data: pendingRequests, error: pendingError } = await supabase
      .from("trip_participant_requests")
      .select(
        "trip_id, trips(id, name, description, start_date, end_date, cover_image_url, creator_id)"
      )
      .eq("user_id", user.id)
      .eq("status", "pending");

    if (pendingError) {
      console.error(
        "❌ Fehler beim Laden offener Anfragen:",
        pendingError.message
      );
    }

    const pendingTripsList: Trip[] = ((pendingRequests ?? []) as unknown as TripRequest[]).flatMap(
      (r) => (Array.isArray(r.trips) ? r.trips : r.trips ? [r.trips] : [])
    );

    setPendingTrips(pendingTripsList);

    // 1. Alle Trips, an denen der User teilnimmt
    const { data: participantTrips, error: participantError } = await supabase
      .from("trip_participants")
      .select(
        "trip_id, is_co_organizer, is_blocked, trips(id, name, description, start_date, end_date, cover_image_url, creator_id, quiz_enabled)"
      )
      .eq("user_id", user.id)
      .or("is_blocked.is.false,is_blocked.is.null");

    if (participantError) {
      console.error(
        "❌ Fehler beim Laden der Teilnehmer-Trips:",
        participantError.message
      );
      return;
    }

    const tripsFromParticipants = (participantTrips as TripJoinEntry[])
      .flatMap((entry) => entry.trips)
      .filter((t): t is Trip => t !== null);

    // Map zur Erkennung, ob User Co-Organisator eines Trips ist
    const newMap = new Map<string, boolean>();
    (participantTrips as TripJoinEntry[]).forEach((entry) => {
      if (entry.trip_id) {
        newMap.set(entry.trip_id, entry.is_co_organizer === true);
      }
    });
    setCoOrganizerMap(newMap);

    // 2. Alle Trips, die der User selbst erstellt hat
    const { data: ownTrips, error: ownTripsError } = await supabase
      .from("trips")
      .select(
        "id, name, description, start_date, end_date, cover_image_url, creator_id, quiz_enabled"
      )
      .eq("creator_id", user.id);

    if (ownTripsError) {
      console.error(
        "❌ Fehler beim Laden der eigenen Trips:",
        ownTripsError.message
      );
      return;
    }

    // 3. Kombinieren, Duplikate entfernen
    const combinedTripsMap = new Map<string, Trip>();
    [...tripsFromParticipants, ...ownTrips].forEach((trip) =>
      combinedTripsMap.set(trip.id, trip)
    );

    const sortedTrips = Array.from(combinedTripsMap.values()).sort(
      (a, b) =>
        new Date(a.start_date).getTime() - new Date(b.start_date).getTime()
    );
    setTrips(sortedTrips);

    // ⬇️ Neu hinzufügen – damit allTrips auch creator/co_orga erkennt
    const tripsWithRoles: TripWithRole[] = sortedTrips.map((trip) => ({
      ...trip,
      role:
        trip.creator_id === user.id
          ? "creator"
          : coOrganizerMap.get(trip.id)
          ? "co_organizer"
          : "participant",
    }));
    setAllTrips(tripsWithRoles);
  };

  useEffect(() => {
    const channel = supabase
      .channel("notifications-dashboard")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "notifications",
        },
        (payload) => {
          const newNotification = payload.new;
          if (
            newNotification.importance === "important" &&
            newNotification.user_id !== user?.id &&
            !newNotification.read_by?.includes(user?.id)
          ) {
            fetchImportantNotifications();
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id]);

  useEffect(() => {
    if (!user) return;

    const fetchFirstName = async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("first_name")
        .eq("id", user.id)
        .single();

      if (!error && data?.first_name) {
        setFirstName(data.first_name);
      } else {
        setFirstName(null);
      }
    };

    (async () => {
      await Promise.all([
        fetchTrips(),
        fetchFirstName(),
        fetchImportantNotifications(),
      ]);
      setLoading(false);
    })();
  }, [user]);

  // 📦 NEU: Wenn ein FAB-Kontext gewählt wird, lade alle "organisierbaren" Reisen
  useEffect(() => {
    if (!dashboardContext || !user?.id) return;

    if (dashboardContext === "add_suggestion") {
      // Vorschläge: alle nicht-blockierten Trips als Teilnehmer laden
      supabase
        .from("trip_participants")
        .select("trips(*)")
        .eq("user_id", user.id)
        .or("is_blocked.is.false,is_blocked.is.null")
        .then(({ data, error }) => {
          if (error) {
            console.error(
              "❌ Fehler beim Laden der Teilnehmer-Trips:",
              error.message
            );
            return;
          }

          const tripsFromParticipantsWithRole: TripWithRole[] = (data ?? [])
            .flatMap((entry: any) => entry.trips)
            .filter((t: any): t is Trip => t !== null)
            .map((trip) => ({
              ...trip,
              role: "participant",
            }));

          setOrganizableTrips(tripsFromParticipantsWithRole); // ✅ korrekt
        });
    } else {
      // Alle anderen Funktionen: nur creator / co-organizer
      getOrganizableTrips(user.id).then(setOrganizableTrips);
    }
  }, [dashboardContext, user?.id]);

  // Organisierbare Reisen auch initial laden, um FAB korrekt zu steuern
  useEffect(() => {
    if (!user?.id) return;
    getAllJoinedTrips(user.id).then(setOrganizableTrips);
  }, [user?.id]);

  // Extras: Offene Aktionen + Cross-Trip Aktivitäts-Feed
  const fetchActivityFeed = async () => {
    if (!user?.id || trips.length === 0) return;

    const now = new Date();
    const ARCHIVE_MS = 14 * 86400000;
    const activeTripIds = trips
      .filter((t) => new Date(t.end_date).getTime() + ARCHIVE_MS >= now.getTime())
      .map((t) => t.id);

    if (activeTripIds.length === 0) return;

    const tripNameMap = new Map(trips.map((t) => [t.id, t.name]));

    // --- Offene Todos ---
    const { data: todoData } = await supabase
      .from("trip_todos")
      .select("id, title, trip_id")
      .in("trip_id", activeTripIds)
      .eq("is_done", false)
      .order("created_at", { ascending: false })
      .limit(6);

    setOpenTodos(
      (todoData ?? []).map((t: any) => ({
        id: t.id,
        title: t.title ?? "Aufgabe",
        trip_id: t.trip_id,
        trip_name: tripNameMap.get(t.trip_id) ?? "",
      }))
    );

    // --- Meine offenen Schulden (Netto-Saldo wie in ExpenseSummary) ---
    const [allSharesRes, userPaymentsRes, paidExpensesRes] = await Promise.all([
      supabase
        .from("trip_expense_shares")
        .select("expense_id, amount")
        .eq("user_id", user.id),
      supabase
        .from("trip_payments")
        .select("amount, trip_id")
        .eq("user_id", user.id)
        .in("trip_id", activeTripIds),
      supabase
        .from("trip_expenses")
        .select("amount, trip_id")
        .eq("payer_id", user.id)
        .in("trip_id", activeTripIds),
    ]);

    const allShareData = allSharesRes.data ?? [];
    const shareExpenseIds = (allShareData as any[]).map((s) => s.expense_id);

    const expTripRes = shareExpenseIds.length > 0
      ? await supabase
          .from("trip_expenses")
          .select("id, trip_id")
          .in("id", shareExpenseIds)
          .in("trip_id", activeTripIds)
      : { data: [] };

    const expTripMap = new Map((expTripRes.data ?? []).map((e: any) => [e.id, e.trip_id]));
    const balanceMap = new Map<string, number>();

    (allShareData as any[]).forEach((s) => {
      const tripId = expTripMap.get(s.expense_id);
      if (!tripId) return;
      balanceMap.set(tripId, (balanceMap.get(tripId) ?? 0) + s.amount);
    });

    (userPaymentsRes.data ?? []).forEach((p: any) => {
      if (!p.trip_id) return;
      balanceMap.set(p.trip_id, (balanceMap.get(p.trip_id) ?? 0) - p.amount);
    });

    (paidExpensesRes.data ?? []).forEach((e: any) => {
      if (!e.trip_id) return;
      balanceMap.set(e.trip_id, (balanceMap.get(e.trip_id) ?? 0) - e.amount);
    });

    setMyDebts(
      Array.from(balanceMap.entries())
        .filter(([, balance]) => balance > 0.01)
        .map(([tripId, balance]) => ({
          trip_id: tripId,
          trip_name: tripNameMap.get(tripId) ?? "",
          total: parseFloat(balance.toFixed(2)),
        }))
    );

    // --- Cross-Trip Aktivitäts-Feed ---
    const [expensesRes, programRes, todosRes] = await Promise.all([
      supabase
        .from("trip_expenses")
        .select("id, title, description, created_at, trip_id")
        .in("trip_id", activeTripIds)
        .order("created_at", { ascending: false })
        .limit(4),
      supabase
        .from("trip_program_items")
        .select("id, title, created_at, trip_id")
        .in("trip_id", activeTripIds)
        .order("created_at", { ascending: false })
        .limit(3),
      supabase
        .from("trip_todos")
        .select("id, title, created_at, trip_id")
        .in("trip_id", activeTripIds)
        .order("created_at", { ascending: false })
        .limit(3),
    ]);

    const feedItems: { id: string; icon: string; description: string; trip_name: string; created_at: string }[] = [];

    (expensesRes.data ?? []).forEach((e: any) => {
      feedItems.push({
        id: `exp-${e.id}`,
        icon: "💸",
        description: e.title || e.description || "Ausgabe",
        trip_name: tripNameMap.get(e.trip_id) ?? "",
        created_at: e.created_at,
      });
    });

    (programRes.data ?? []).forEach((p: any) => {
      feedItems.push({
        id: `prog-${p.id}`,
        icon: "📅",
        description: p.title ?? "Programmpunkt",
        trip_name: tripNameMap.get(p.trip_id) ?? "",
        created_at: p.created_at,
      });
    });

    (todosRes.data ?? []).forEach((t: any) => {
      feedItems.push({
        id: `todo-${t.id}`,
        icon: "✅",
        description: t.title ?? "Aufgabe",
        trip_name: tripNameMap.get(t.trip_id) ?? "",
        created_at: t.created_at,
      });
    });

    feedItems.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    setCrossFeed(feedItems.slice(0, 8));
  };

  useEffect(() => {
    fetchActivityFeed();
  }, [trips, user?.id]);

  const fetchImportantNotifications = async () => {
    if (!user?.id) return;

    const { data, error } = await supabase
      .from("notifications")
      .select(
        "id, title, message, user_id, created_at, trip_id, trip:trips(name), author:profiles(first_name, last_name)"
      )
      .eq("importance", "important")
      .not("read_by", "cs", `{${user.id}}`)
      .neq("user_id", user.id);

    if (!error && data) {
      setImportantNotifications(
        data.map((n) => ({
          id: n.id,
          title: n.title,
          message: n.message,
          user_id: n.user_id,
          created_at: n.created_at, // ✅ NEU
          trip_id: n.trip_id, // ✅ NEU
          trip: Array.isArray(n.trip) ? n.trip[0] : n.trip,
          author: Array.isArray(n.author) ? n.author[0] : n.author,
        }))
      );
    }
  };

  const markAsRead = async (notificationId: string) => {
    const { error } = await supabase.rpc("mark_notification_as_read", {
      notif_id: notificationId,
      reader_id: user!.id,
    });

    if (error) {
      console.error("❌ Fehler beim Aktualisieren:", error.message);
    } else {
      setImportantNotifications(
        (prev) => prev.filter((n) => n.id !== notificationId) // Optional: Ausblenden nach Klick
      );
    }
  };

  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel("trip-changes-dashboard")
      .on(
        "postgres_changes",
        {
          event: "*", // INSERT, UPDATE, DELETE
          schema: "public",
          table: "trip_participants",
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          fetchTrips();
        }
      )
      .on(
        "postgres_changes",
        {
          event: "DELETE",
          schema: "public",
          table: "trips",
        },
        () => {
          fetchTrips();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id]);

  // Realtime-Sync für den Aktivitäts-Feed: Ausgaben, Programmpunkte, Todos
  useEffect(() => {
    if (!user?.id || trips.length === 0) return;

    const channel = supabase
      .channel("dashboard-feed-sync")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "trip_expenses" },
        () => { fetchActivityFeed(); }
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "trip_program_items" },
        () => { fetchActivityFeed(); }
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "trip_todos" },
        () => { fetchActivityFeed(); }
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "trip_expense_shares" },
        () => { fetchActivityFeed(); }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, trips]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0d1117] flex items-center justify-center">
        <span className="text-slate-500 text-sm animate-pulse">Lade deine Reisen...</span>
      </div>
    );
  }

  const now = new Date();
  const ARCHIVE_AFTER_MS = ARCHIVE_AFTER_DAYS * 86400000;

  const activeTrips = trips.filter(
    (t) => new Date(t.end_date).getTime() + ARCHIVE_AFTER_MS >= now.getTime()
  );
  const archivedTrips = trips.filter(
    (t) => new Date(t.end_date).getTime() + ARCHIVE_AFTER_MS < now.getTime()
  );

  // Hero-Reise: laufende Reise bevorzugen, sonst nächste bevorstehende
  const inProgressTrips = activeTrips.filter(
    (t) => new Date(t.start_date) <= now && new Date(t.end_date) >= now
  );
  const upcomingTrips = activeTrips
    .filter((t) => new Date(t.start_date) > now)
    .sort((a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime());

  const heroTrip = inProgressTrips[0] ?? upcomingTrips[0] ?? activeTrips[0] ?? null;
  const otherActiveTrips = activeTrips.filter((t) => t.id !== heroTrip?.id);

  // Countdown-Text für Hero-Karte
  function getHeroCountdown(trip: Trip): { text: string; color: string } {
    const phase = getTripPhase(trip.start_date, trip.end_date);
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startStart = new Date(
      new Date(trip.start_date).getFullYear(),
      new Date(trip.start_date).getMonth(),
      new Date(trip.start_date).getDate()
    );
    const daysToStart = Math.ceil((startStart.getTime() - todayStart.getTime()) / 86400000);

    if (phase === "pre_trip_early" || phase === "pre_trip_late")
      return { text: `Noch ${daysToStart} Tage`, color: "text-amber-400" };
    if (phase === "pre_trip_final")
      return { text: daysToStart === 1 ? "Morgen geht's los!" : `Noch ${daysToStart} Tage`, color: "text-amber-400" };
    if (phase === "travel_day")
      return { text: "Heute geht's los! 🎉", color: "text-amber-400" };
    if (phase === "in_progress")
      return { text: "Unterwegs ✈️", color: "text-emerald-400" };
    if (phase === "last_day")
      return { text: "Letzter Reisetag", color: "text-sky-400" };
    return { text: "", color: "" };
  }

  function formatShortDate(dateStr: string): string {
    return new Date(dateStr).toLocaleDateString("de-CH", { day: "numeric", month: "short" });
  }

  const isEmpty =
    activeTrips.length === 0 && pendingTrips.length === 0 && archivedTrips.length === 0;

  return (
    <div className="min-h-screen bg-[#0d1117]">
      <div className="max-w-4xl mx-auto pt-20 pb-24 px-4 sm:px-6">

        {/* Begrüßung */}
        <div className="mb-5">
          {profile?.first_name ? (
            <h1 className="text-2xl font-bold text-slate-100">
              Hej{" "}
              <Link to="/profile" className="text-sky-400 hover:text-sky-300 transition-colors">
                {profile.first_name}
              </Link>
              {" "}👋
            </h1>
          ) : (
            <p className="text-xl text-slate-300">
              Hej und willkommen!{" "}
              <Link to="/profile" className="text-sky-400 hover:text-sky-300 underline">
                Ergänze deinen Namen
              </Link>
            </p>
          )}
        </div>

        {/* Wichtige Nachrichten */}
        {importantNotifications.length > 0 && (
          <div className="mb-5 space-y-3">
            <h2 className="text-xs uppercase tracking-widest text-amber-500 font-medium">
              ❗ Wichtige Nachrichten
            </h2>
            {importantNotifications.map((n) => {
              const isOrga = !!(
                n.trip_id &&
                (coOrganizerMap.get(n.trip_id) === true ||
                  trips.find((t) => t.id === n.trip_id)?.creator_id === user?.id)
              );
              return (
                <NotificationCard
                  key={n.id}
                  id={n.id}
                  title={n.title}
                  message={n.message}
                  importance="important"
                  created_at={n.created_at}
                  isRead={false}
                  tripName={n.trip?.name}
                  tripId={n.trip_id}
                  author={n.author}
                  onMarkAsRead={markAsRead}
                  isOrga={isOrga}
                  onDelete={async (id) => {
                    const confirmed = await showConfirm({
                      title: "Nachricht löschen",
                      message: "❌ Diese Nachricht wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.",
                      confirmLabel: "Löschen",
                      cancelLabel: "Abbrechen",
                      icon: "warning",
                    });
                    if (!confirmed) return;
                    const { error } = await supabase.from("notifications").delete().eq("id", id);
                    if (error) {
                      console.error("❌ Fehler beim Löschen:", error.message);
                    } else {
                      await fetchImportantNotifications();
                    }
                  }}
                />
              );
            })}
          </div>
        )}

        {/* Empty State */}
        {isEmpty && (
          <div className="mt-6 bg-gradient-to-br from-sky-900/30 to-slate-800/50 border border-[#2a3347] rounded-2xl p-8 text-center space-y-4">
            <div className="text-5xl mb-2">🌍</div>
            <h2 className="text-2xl font-bold text-slate-100">Gemeinsam erleben mit Svevana</h2>
            <p className="text-slate-400 text-sm max-w-sm mx-auto">
              Deine persönliche Reiseplattform – plane, teile und verwalte deine Trips.
            </p>
            {gateCreate && (
              <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 text-amber-400 p-3 text-sm">
                Du bist aktuell auf der Warteliste. Teile uns kurz mit, warum du Svevana testen möchtest. 🙌
              </div>
            )}
            <button
              onClick={() => gateCreate ? setShowWaitlistModal(true) : setShowCreateModal(true)}
              className="bg-sky-500 hover:bg-sky-400 text-white py-2.5 px-6 rounded-xl font-medium transition-colors mt-2"
            >
              {gateCreate ? "Warum möchtest du mitmachen?" : "+ Neue Reise erstellen"}
            </button>
            {!profile?.first_name && (
              <div className="pt-2 text-sm">
                <Link to="/profile" className="text-sky-400 hover:text-sky-300 underline">
                  ➕ Ergänze dein Profil für ein persönlicheres Erlebnis
                </Link>
              </div>
            )}
          </div>
        )}

        {/* Hero-Karte: Nächste / aktuelle Reise */}
        {heroTrip && (
          <div className="mb-5">
            <h2 className="text-xs uppercase tracking-widest text-slate-500 font-medium mb-3">
              ✈️ Nächste Reise
            </h2>
            <div
              onClick={() => navigate(`/trip/${heroTrip.id}`)}
              className="bg-[#161b27] rounded-2xl overflow-hidden border border-[#2a3347] hover:border-sky-500/30 hover:-translate-y-0.5 transition-all duration-200 cursor-pointer"
            >
              {heroTrip.cover_image_url ? (
                <div className="relative w-full h-56">
                  <img
                    src={heroTrip.cover_image_url}
                    alt={heroTrip.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#161b27] via-[#161b27]/40 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h2 className="text-xl font-bold text-white drop-shadow-lg">{heroTrip.name}</h2>
                    {(() => {
                      const { text, color } = getHeroCountdown(heroTrip);
                      return text ? <p className={`text-sm font-semibold mt-1 ${color}`}>{text}</p> : null;
                    })()}
                  </div>
                </div>
              ) : (
                <div className="relative w-full h-40 bg-gradient-to-br from-sky-900/40 to-slate-800 flex flex-col items-center justify-center gap-2">
                  <span className="text-4xl">🌍</span>
                  <h2 className="text-lg font-bold text-slate-100">{heroTrip.name}</h2>
                  {(() => {
                    const { text, color } = getHeroCountdown(heroTrip);
                    return text ? <p className={`text-sm font-semibold ${color}`}>{text}</p> : null;
                  })()}
                </div>
              )}
              <div className="p-4 pt-3 flex items-center justify-between">
                <p className="text-xs text-slate-500">
                  {formatShortDate(heroTrip.start_date)} – {formatShortDate(heroTrip.end_date)}
                </p>
                <span className="text-sky-400 text-sm font-medium">Zur Reise →</span>
              </div>
            </div>
          </div>
        )}

        {/* ✨ KI Reiseplanung */}
        {showAiPlanning && (
          <div className="mb-5">
            <button
              onClick={() => setShowAiPlanningModal(true)}
              className="w-full bg-[#161b27] border border-[#2a3347] rounded-2xl p-4 flex items-center gap-4 hover:border-sky-500/30 transition-colors text-left"
            >
              <div className="w-10 h-10 rounded-xl bg-sky-500/10 flex items-center justify-center text-sky-400 text-xl shrink-0">
                ✨
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-100">KI Reiseplanung</p>
                <p className="text-xs text-slate-500">Neue Reise automatisch planen lassen</p>
              </div>
              <span className="ml-auto text-slate-600 text-sm">→</span>
            </button>
          </div>
        )}

        {/* 📰 Aktivitäts-Feed */}
        {crossFeed.length > 0 && (
          <div className="mb-5">
            <h2 className="text-xs uppercase tracking-widest text-slate-500 font-medium mb-3">
              📰 Zuletzt passiert
            </h2>
            <div className="bg-[#161b27] rounded-2xl border border-[#2a3347] overflow-hidden divide-y divide-[#2a3347]">
              {crossFeed.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center gap-3 p-4 hover:bg-[#1e2639] transition-colors"
                >
                  <span className="text-lg flex-shrink-0">{item.icon}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-300 truncate">{item.description}</p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {item.trip_name}
                      {item.trip_name && " · "}
                      {formatDistanceToNow(new Date(item.created_at), { addSuffix: true, locale: de })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ⚡ Offene Aktionen */}
        {(openTodos.length > 0 || myDebts.length > 0) && (
          <div className="mb-5">
            <h2 className="text-xs uppercase tracking-widest text-slate-500 font-medium mb-3">
              ⚡ Offene Aktionen
            </h2>
            <div className="bg-[#161b27] rounded-xl border border-[#2a3347] divide-y divide-[#2a3347] overflow-hidden">
              {myDebts.map((debt) => (
                <div
                  key={debt.trip_id}
                  onClick={() => navigate(`/trip/${debt.trip_id}?tab=accounting`)}
                  className="flex items-center gap-3 p-4 hover:bg-[#1e2639] transition-colors cursor-pointer"
                >
                  <span className="text-lg flex-shrink-0">💳</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-300 truncate">
                      Offene Schuld – CHF {debt.total.toFixed(2)}
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5">{debt.trip_name}</p>
                  </div>
                  <span className="text-[11px] text-red-400 font-medium flex-shrink-0 px-2 py-0.5 rounded-full bg-red-400/10 border border-red-400/20">
                    Schuld
                  </span>
                </div>
              ))}
              {openTodos.slice(0, 4).map((todo) => (
                <div
                  key={todo.id}
                  onClick={() => navigate(`/trip/${todo.trip_id}`)}
                  className="flex items-center gap-3 p-4 hover:bg-[#1e2639] transition-colors cursor-pointer"
                >
                  <span className="text-lg flex-shrink-0">📝</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-300 truncate">{todo.title}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{todo.trip_name}</p>
                  </div>
                  <span className="text-[11px] text-amber-400 font-medium flex-shrink-0 px-2 py-0.5 rounded-full bg-amber-400/10 border border-amber-400/20">
                    Offen
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Weitere aktive Reisen – horizontaler Scroll */}
        {otherActiveTrips.length > 0 && (
          <div className="mb-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-xs uppercase tracking-widest text-slate-500 font-medium">
                Weitere Reisen
              </h2>
              <Link to="/trips" className="text-xs text-sky-400 hover:text-sky-300 transition-colors">
                Alle →
              </Link>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4">
              {otherActiveTrips.map((trip) => (
                <div
                  key={trip.id}
                  onClick={() => navigate(`/trip/${trip.id}`)}
                  className="flex-shrink-0 w-44 bg-[#161b27] rounded-xl overflow-hidden border border-[#2a3347] hover:border-sky-500/30 transition-colors cursor-pointer"
                >
                  {trip.cover_image_url ? (
                    <img src={trip.cover_image_url} alt={trip.name} className="w-full h-28 object-cover" />
                  ) : (
                    <div className="w-full h-28 bg-gradient-to-br from-sky-900/30 to-slate-800 flex items-center justify-center">
                      <span className="text-2xl">🌍</span>
                    </div>
                  )}
                  <div className="p-3">
                    <p className="text-sm font-medium text-slate-200 truncate">{trip.name}</p>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      {formatShortDate(trip.start_date)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Angefragte Reisen – kompakte Liste */}
        {pendingTrips.length > 0 && (
          <div className="mb-5">
            <h2 className="text-xs uppercase tracking-widest text-slate-500 font-medium mb-3">
              ⏳ Angefragte Reisen
            </h2>
            <div className="bg-[#161b27] rounded-xl border border-[#2a3347] divide-y divide-[#2a3347]">
              {pendingTrips.map((trip) => (
                <div key={trip.id} className="flex items-center gap-3 p-4">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-300 truncate">{trip.name}</p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      <span className="text-amber-400">🕓 Warten auf Freigabe</span>
                      {" · "}{formatShortDate(trip.start_date)}
                    </p>
                  </div>
                  <button
                    onClick={async () => {
                      await supabase
                        .from("trip_participant_requests")
                        .delete()
                        .eq("trip_id", trip.id)
                        .eq("user_id", user!.id);
                      await fetchTrips();
                    }}
                    className="text-xs text-slate-500 hover:text-red-400 transition-colors flex-shrink-0"
                  >
                    Zurückziehen
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        
        {/* Link zu allen Reisen */}
        
        {/* -- Temporär ausgeschaltet, da Redundant zur Reise Übersicht
        {(activeTrips.length + archivedTrips.length) > 0 && (
          <div className="mt-2 mb-5">
            <Link
              to="/trips"
              className="block text-center py-3 text-sm text-slate-500 hover:text-sky-400 transition-colors border border-[#2a3347] rounded-xl hover:border-sky-500/30"
            >
              Alle Reisen anzeigen →
            </Link>
          </div>
        )}
        */}

        {/* Umfragen */}
        {activeTrips.length > 0 && (
          <div className="mb-5">
            <PollUserView />
          </div>
        )}

                {/* Feedback CTA */}
        <div className="text-center mt-6 py-2">
          <a
            href="https://svevana.app/feedback"
            target="_self"
            rel="noopener noreferrer"
            className="text-xs text-slate-600 hover:text-slate-400 underline transition-colors"
          >
            💡 Dein Feedback zu Svevana abgeben
          </a>
        </div>

        {/* Blog (nur für Creator/Admin) */}
        <div className="mt-4">
          {(profile?.role === "creator" || profile?.role === "admin") && (
            <button
              onClick={() => setShowBlog(!showBlog)}
              className="text-xs text-slate-600 hover:text-slate-400 underline transition-colors"
            >
              {showBlog ? "Blog ausblenden" : "Svevana Blog anzeigen"}
            </button>
          )}
          {showBlog && (
            <div className="mt-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-bold text-slate-200">Svevana Blog</h2>
                {(profile?.role === "creator" || profile?.role === "admin") && (
                  <button
                    onClick={() => navigate("/create-blog")}
                    className="bg-sky-500 hover:bg-sky-400 text-white py-1.5 px-3 rounded-lg text-sm transition-colors"
                  >
                    + Neuer Beitrag
                  </button>
                )}
              </div>
              <BlogPosts showCreateButton={false} />
            </div>
          )}
        </div>



  


      <FloatingActionButton
      actions={[
        {
          label: "🎒 Neue Reise",
          onClick: () => {
            if (gateCreate) {
              setShowWaitlistModal(true);
            } else {
              setShowCreateModal(true);
            }
          },
        },
        ...(hasOrgaTrip
          ? [
              {
                label: "📅 Programmpunkt hinzufügen",
                onClick: () => {
                  setDashboardContext("add_program_item");
                  setShowTripSelectModal(true);
                },
              },
              {
                label: "📝 Aufgabe hinzufügen",
                onClick: () => {
                  setDashboardContext("add_task");
                  setShowTripSelectModal(true);
                },
              },
              {
                label: "📣 Neue Benachrichtigung",
                onClick: () => {
                  setDashboardContext("add_notification");
                  setShowTripSelectModal(true);
                },
              },
              {
                label: "📤 Dokument hochladen",
                onClick: () => {
                  setDashboardContext("add_document");
                  setShowTripSelectModal(true);
                },
              },
              {
                label: "💰 Ausgabe erfassen",
                onClick: () => {
                  setDashboardContext("add_expense");
                  setShowTripSelectModal(true);
                },
              },
              {
                label: "💳 Zahlung erfassen",
                onClick: () => {
                  setDashboardContext("add_payment");
                  setShowTripSelectModal(true);
                },
              },
            ]
          : []),
        ...(hasAnyTrip
          ? [
              {
                label: "💡 Vorschlag einreichen",
                onClick: () => {
                  setDashboardContext("add_suggestion");
                  setShowTripSelectModal(true);
                },
              },
            ]
          : []),
      ]}
      localStorageKey="seen_fab_dashboard"
      autoExpandDelay={1000}
    />


    <WaitlistModalInline
      open={showWaitlistModal}
      onClose={() => setShowWaitlistModal(false)}
      onSubmit={handleWaitlistSubmit}
    />


      {showCreateModal && (
        <CreateTripModal
          onClose={() => setShowCreateModal(false)}
          onCreated={async () => {
            await fetchTrips();
            const updated = await getAllJoinedTrips(user!.id);
            setAllTrips(updated);
          }}
        />
      )}
      {user?.id && <PushPermissionModal userId={user.id} />}
      {showAiPlanningModal && user?.id && (
        <AiTripPlanningModal
          mode="new_trip"
          userId={user.id}
          onClose={async (created) => {
            setShowAiPlanningModal(false);
            if (created) {
              await fetchTrips();
              const updated = await getAllJoinedTrips(user.id);
              setAllTrips(updated);
            }
          }}
        />
      )}
      <TripSelectModal
        open={showTripSelectModal}
        onClose={() => {
          setShowTripSelectModal(false);
          setDashboardContext(null); // optional zurücksetzen
        }}
        trips={organizableTrips}
        onSelectTrip={(tripId: string) => {
          setShowTripSelectModal(false);

          switch (dashboardContext) {
            case "add_program_item":
              navigate(`/trip/${tripId}?tab=program&add=new`);
              break;
            case "add_task":
              navigate(`/trip/${tripId}?tab=overview&add=task`);
              break;
            case "add_notification":
              navigate(`/trip/${tripId}?tab=overview&add=notification`);
              break;
            case "add_suggestion":
              navigate(`/trip/${tripId}?tab=overview&add=suggestion`);
              break;
            case "add_document":
              navigate(`/trip/${tripId}?tab=documents&add=document`);
              break;
            case "add_expense":
              navigate(`/trip/${tripId}?tab=accounting&add=expense`);
              break;
            case "add_payment":
              navigate(`/trip/${tripId}?tab=accounting&add=payment`);
              break;
          }

          setDashboardContext(null);
        }}
      />
    </div>
    </div>
  );
}

function WaitlistModalInline({
      open,
      onClose,
      onSubmit,
    }: {
      open: boolean;
      onClose: () => void;
      onSubmit: (message: string) => Promise<void> | void;
    }) {
      const [message, setMessage] = useState("");
      const [sending, setSending] = useState(false);

      if (!open) return null;

      const disabled = message.trim().length === 0 || message.length > 500;

      const submit = async () => {
        setSending(true);
        try {
          await onSubmit(message.trim());
          setMessage("");
        } finally {
          setSending(false);
        }
      };

      return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-[#161b27] border border-[#2a3347] w-full max-w-lg rounded-2xl p-6 shadow-xl">
            <h2 className="text-lg font-semibold mb-2 text-slate-100">Warteliste für Svevana</h2>
            <p className="text-sm text-slate-400 mb-3">
              Kurz: Warum möchtest du Svevana testen? (max. 500 Zeichen)
            </p>
            <textarea
              className="w-full border border-[#2a3347] rounded-lg p-3 min-h-[120px] outline-none bg-[#0d1117] text-slate-100 placeholder:text-slate-600 focus:border-sky-400"
              value={message}
              maxLength={500}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Dein Kommentar…"
            />
            <div className="flex justify-between items-center text-xs text-slate-500 mt-2">
              <span>{message.length}/500</span>
            </div>
            <div className="mt-4 flex gap-2 justify-end">
              <button
                className="px-3 py-2 rounded-lg border border-[#2a3347] text-slate-300 bg-[#1e2639] hover:bg-[#2a3347]"
                onClick={onClose}
                disabled={sending}
              >
                Abbrechen
              </button>
              <button
                className="px-4 py-2 rounded-lg bg-sky-500 text-white hover:bg-sky-600 disabled:opacity-50"
                disabled={disabled || sending}
                onClick={submit}
              >
                {sending ? "Sende…" : "Senden"}
              </button>
            </div>
          </div>
        </div>
      );
    }